import { z } from "zod";
import { ACCESS_DEFAULTS_MAX, type AccessCodeKind } from "./access-codes";
import type { AcCardStatus } from "./accreditation";
import { PAGE_SIZE_DEFAULT, PAGE_SIZE_MAX } from "./pagination";
import { settingsPaginationQuery } from "./organization-types";

/**
 * Zone access rules (`access_rules` + `access_rule_codes`, migration 0027) —
 * what a card grants is decided by the MOST SPECIFIC rule matching its
 * (category, function, national federation):
 *
 *   category + function + federation  >  category + function
 *     >  category + federation  >  category
 *
 * "National federation" is the card's `sub_agency_id` (the NOC organisation
 * type labels its sub-agencies "National federation": "National Muay
 * Federation" is a sub-agency row). A category-only rule is the base, the
 * successor of the per-category default access.
 *
 * Resolved in SQL only (`fn_access_rule_codes`, and the same ORDER BY in the
 * set-based apply), never in a frontend. A card follows its rule — on create,
 * and when its category / function / federation changes (trigger) — unless
 * its access was edited by hand (`ac_cards.access_custom`). A rule change is
 * PREVIEWED first and applied to every following card, all statuses, with
 * the `expected_count` the preview showed (drift → 409
 * `ACCESS_RULE_CARDS_CHANGED`). Cards with custom access are never touched.
 *
 * ADMIN reads, SUPER_ADMIN writes (Game Settings).
 */

/** A rule may name at most this many codes (the same ceiling as a card's own access). */
export const ACCESS_RULE_CODES_MAX = ACCESS_DEFAULTS_MAX;

/** How specific a rule is, most specific first — the resolution order. */
export const ACCESS_RULE_LEVELS = ["CATEGORY_FUNCTION_FEDERATION", "CATEGORY_FUNCTION", "CATEGORY_FEDERATION", "CATEGORY"] as const;
export type AccessRuleLevel = (typeof ACCESS_RULE_LEVELS)[number];

export const accessRuleLevel = (r: { sub_function_id: string | null; sub_agency_id: string | null }): AccessRuleLevel =>
	r.sub_function_id && r.sub_agency_id ? "CATEGORY_FUNCTION_FEDERATION" : r.sub_function_id ? "CATEGORY_FUNCTION" : r.sub_agency_id ? "CATEGORY_FEDERATION" : "CATEGORY";

/** One code of a rule, in kind order then sort_order then code. `is_active: false` = deactivated since: not granted any more. */
export interface AccessRuleCode {
	access_code_id: string;
	kind: AccessCodeKind;
	code: string;
	is_active: boolean;
}

/** One row of `GET /access-rules` and `GET /access-rules/:id`. */
export interface AccessRuleListItem {
	access_rule_id: string;
	level: AccessRuleLevel;
	main_function: {
		main_function_id: string;
		main_function_code: string;
		main_function_name: string;
		card_color: string | null;
		card_ink_color: string | null;
	};
	sub_function: { sub_function_id: string; sub_function_code: string; sub_function_name: string } | null;
	/** The national federation (the card's sub-agency). */
	sub_agency: { sub_agency_id: string; name: string } | null;
	access_codes: AccessRuleCode[];
	created_at: string;
	updated_at: string;
}

/**
 * `GET /access-rules` — offset pages (a small admin table), ordered by
 * category (sort order, code), then most general first (the base rule leads
 * its category), then function and federation name.
 */
export const listAccessRulesQuery = settingsPaginationQuery.extend({
	main_function_id: z.string().uuid().optional(),
	sub_agency_id: z.string().uuid().optional(),
});
export type ListAccessRulesQuery = z.infer<typeof listAccessRulesQuery>;

const accessCodeIds = z
	.array(z.string().uuid())
	.max(ACCESS_RULE_CODES_MAX)
	.refine((ids) => new Set(ids.map((id) => id.toLowerCase())).size === ids.length, "Each access code may appear once");

/** The count the preview showed — the apply refuses (409 ACCESS_RULE_CARDS_CHANGED) when it differs. */
const expectedCount = z.coerce.number().int().min(0);
const reasonField = z.string().trim().min(1).max(300);

// ── The change being previewed / applied ────────────────────────────────────

/** A new rule. `sub_function_id` must belong to the category; null / omitted = any. */
export const accessRuleCreateFields = z.object({
	main_function_id: z.string().uuid(),
	sub_function_id: z.string().uuid().nullable().optional(),
	sub_agency_id: z.string().uuid().nullable().optional(),
	access_code_ids: accessCodeIds,
});

/**
 * `POST /access-rules/preview` and `/preview/cards` — the change, not yet
 * written. `update` changes the codes only (the key — category, function,
 * federation — never changes: delete and create instead).
 */
export const accessRuleChange = z.discriminatedUnion("op", [
	accessRuleCreateFields.extend({ op: z.literal("create") }),
	z.object({ op: z.literal("update"), access_rule_id: z.string().uuid(), access_code_ids: accessCodeIds }),
	z.object({ op: z.literal("delete"), access_rule_id: z.string().uuid() }),
]);
export type AccessRuleChange = z.input<typeof accessRuleChange>;

/**
 * `POST /access-rules/preview` — ONE aggregate over the category's cards.
 *
 *   following_count  cards that follow their rule and whose access WILL
 *                    change (all statuses, approved included)
 *   custom_count     cards with custom access this rule governs (before or
 *                    after the change) — left alone
 *
 * Send `following_count` back as `expected_count` to apply.
 */
export interface AccessRulePreview {
	following_count: number;
	custom_count: number;
}

export const ACCESS_RULE_PREVIEW_GROUPS = ["following", "custom"] as const;
export type AccessRulePreviewGroup = (typeof ACCESS_RULE_PREVIEW_GROUPS)[number];

/** `POST /access-rules/preview/cards` — one keyset page (by registration number) of either group. */
export const accessRulePreviewCardsBody = z.object({
	change: accessRuleChange,
	group: z.enum(ACCESS_RULE_PREVIEW_GROUPS),
	/** `next_cursor` of the previous page, exactly as received. */
	cursor: z.string().regex(/^[0-9]{1,10}$/).optional(),
	limit: z.number().int().min(1).max(PAGE_SIZE_MAX).default(PAGE_SIZE_DEFAULT),
});
export type AccessRulePreviewCardsBody = z.input<typeof accessRulePreviewCardsBody>;

/** One person in the preview list. Codes are the printed codes, in kind order. */
export interface AccessRulePreviewCard {
	ac_card_id: string;
	registration_no_display: string;
	printed_name: string;
	status: AcCardStatus;
	responsible_organization_code: string | null;
	sub_function_name: string | null;
	sub_agency_name: string | null;
	current_codes: string[];
	/** What the card gets after the change (for `custom`: what it would get if it followed the rule). */
	new_codes: string[];
}

export interface AccessRulePreviewCards {
	rows: AccessRulePreviewCard[];
	next_cursor: string | null;
}

// ── Writes (SUPER_ADMIN) ────────────────────────────────────────────────────

/** `POST /access-rules` — creates the rule AND re-applies every following card it captures. */
export const createAccessRuleBody = accessRuleCreateFields.extend({
	expected_count: expectedCount,
	reason: reasonField.optional(),
});
export type CreateAccessRuleBody = z.input<typeof createAccessRuleBody>;

/** `PUT /access-rules/:id` — the WHOLE code set; re-applies every following card it governs. */
export const updateAccessRuleBody = z.object({
	access_code_ids: accessCodeIds,
	expected_count: expectedCount,
	reason: reasonField.optional(),
});
export type UpdateAccessRuleBody = z.input<typeof updateAccessRuleBody>;

/** `DELETE /access-rules/:id?expected_count=&reason=` — its following cards move to the next most specific rule. */
export const deleteAccessRuleQuery = z.object({
	expected_count: expectedCount,
	reason: reasonField.optional(),
});
export type DeleteAccessRuleQuery = z.input<typeof deleteAccessRuleQuery>;

/** What a rule write answers: the rule (null after a delete) and how many cards were re-applied. */
export interface AccessRuleWriteResult {
	rule: AccessRuleListItem | null;
	cards_updated: number;
}

// ── A card's own access ─────────────────────────────────────────────────────

/**
 * `PUT /accreditations/:id/access` and `POST /accreditations/:id/access/reset`.
 * `access_custom` is true when the card's access differs from its rule (a
 * hand edit that equals the rule leaves the card following it).
 */
export interface AccreditationAccessResult {
	ac_card_id: string;
	access_custom: boolean;
	access: Array<{ access_code_id: string; kind: AccessCodeKind; code: string; name: string }>;
}

/** `POST /accreditations/:id/access/reset` — clears `access_custom` and re-applies the rule. */
export const resetCardAccessBody = z.object({
	reason: reasonField.optional(),
});
export type ResetCardAccessBody = z.input<typeof resetCardAccessBody>;
